import React, { useState, useRef, useCallback, useEffect } from 'react';
import CompassDial from './CompassDial';
import './SpatialFilterControls.css';

function useDebouncedCallback(fn, delay) {
  const timerRef = useRef(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  return useCallback((...args) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fnRef.current(...args), delay);
  }, [delay]);
}

const SpatialFilterControls = ({
  radius,
  onRadiusChange,
  azimuth,
  onAzimuthChange,
  sliceWidth,
  onSliceWidthChange,
  sliceEnabled,
  onSliceEnabledChange,
  holeCount,
  loading,
  onSnapView,
}) => {
  const [localRadius, setLocalRadius] = useState(radius);
  const [localSliceWidth, setLocalSliceWidth] = useState(sliceWidth);
  const [localAzimuth, setLocalAzimuth] = useState(azimuth);

  useEffect(() => { setLocalRadius(radius); }, [radius]);
  useEffect(() => { setLocalSliceWidth(sliceWidth); }, [sliceWidth]);
  useEffect(() => { setLocalAzimuth(azimuth); }, [azimuth]);

  const debouncedRadiusChange = useDebouncedCallback(onRadiusChange, 300);
  const debouncedSliceWidthChange = useDebouncedCallback(onSliceWidthChange, 300);
  const debouncedAzimuthChange = useDebouncedCallback(onAzimuthChange, 300);

  const handleRadiusInput = (e) => {
    const val = parseInt(e.target.value, 10);
    setLocalRadius(val);
    debouncedRadiusChange(val);
  };

  const handleSliceWidthInput = (e) => {
    const val = parseInt(e.target.value, 10);
    setLocalSliceWidth(val);
    debouncedSliceWidthChange(val);
  };

  const handleAzimuthInput = (val) => {
    setLocalAzimuth(val);
    debouncedAzimuthChange(val);
  };

  return (
    <div className="spatial-filter-controls">
      {/* Row 1: Radius */}
      <div className="spatial-row">
        <label className="spatial-label">Radius</label>
        <input
          type="range"
          className="spatial-slider"
          min="0" max="1000" step="50"
          value={localRadius}
          onChange={handleRadiusInput}
        />
        <span className="spatial-value">{localRadius}m</span>
        {holeCount > 0 && (
          <span className={`spatial-badge ${holeCount >= 25 ? 'warn' : ''}`}>
            {loading ? '...' : `${holeCount} hole${holeCount !== 1 ? 's' : ''}`}
          </span>
        )}
      </div>

      {/* Row 2: Compass + slice controls */}
      <div className="spatial-row spatial-row-slice">
        <CompassDial
          azimuth={localAzimuth}
          onChange={handleAzimuthInput}
          onSnapView={onSnapView}
          disabled={!sliceEnabled || localRadius === 0}
        />
        <div className="spatial-slice-group">
          <label className="spatial-slice-toggle">
            <input
              type="checkbox"
              checked={sliceEnabled}
              onChange={(e) => onSliceEnabledChange(e.target.checked)}
              disabled={localRadius === 0}
            />
            <span>Slice</span>
          </label>
          <div className="spatial-slice-slider-row">
            <input
              type="range"
              className="spatial-slider"
              min="10" max="1000" step="10"
              value={localSliceWidth}
              onChange={handleSliceWidthInput}
              disabled={!sliceEnabled || localRadius === 0}
            />
            <span className="spatial-value">{localSliceWidth}m</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SpatialFilterControls);
